import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import pb, { apiCall } from '../services/pocketbase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertCircle,
  FiArchive,
  FiCheck,
  FiEdit3,
  FiEye,
  FiList,
  FiPlus,
  FiSearch,
  FiX
} = FiIcons;

const emptyFilters = {
  visibility_scope: '',
  workflow_status: '',
  app_id: ''
};

const escapeFilterValue = (value) => String(value || '')
  .replace(/\\/g, '\\\\')
  .replace(/"/g, '\\"');

const Playlists = () => {
  const [playlists, setPlaylists] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  const [selectedPlaylists, setSelectedPlaylists] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    totalItems: 0,
    totalPages: 0
  });

  useEffect(() => {
    loadApps();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    loadPlaylists();
  }, [debouncedSearchTerm, filters, pagination.page]);

  const loadApps = async () => {
    try {
      const result = await apiCall(() => pb.collection('apps').getFullList({
        sort: 'app_name',
        filter: 'is_active = true'
      }));
      setApps(result);
    } catch (error) {
      console.error('Failed to load apps for Topic filters:', error);
    }
  };

  const buildFilter = () => {
    const clauses = ['workflow_status != "archived"'];

    if (debouncedSearchTerm) {
      const search = escapeFilterValue(debouncedSearchTerm);
      clauses.push(`(name ~ "${search}" || description ~ "${search}" || tags ~ "${search}")`);
    }

    if (filters.visibility_scope) {
      clauses.push(`visibility_scope = "${escapeFilterValue(filters.visibility_scope)}"`);
    }
    if (filters.workflow_status) {
      clauses.push(`workflow_status = "${escapeFilterValue(filters.workflow_status)}"`);
    }
    if (filters.app_id) {
      clauses.push(`app_ids ?= "${escapeFilterValue(filters.app_id)}"`);
    }

    return clauses.join(' && ');
  };

  const loadPlaylists = async () => {
    try {
      setLoading(true);
      setLoadError('');

      const result = await apiCall(() => pb.collection('playlists').getList(
        pagination.page,
        pagination.perPage,
        {
          filter: buildFilter(),
          sort: '-updated',
          expand: 'app_ids'
        }
      ));

      setPlaylists(result.items);
      setPagination(prev => ({
        ...prev,
        totalItems: result.totalItems,
        totalPages: result.totalPages
      }));
      setSelectedPlaylists(current => {
        const visibleIds = new Set(result.items.map(playlist => playlist.id));
        return new Set(Array.from(current).filter(id => visibleIds.has(id)));
      });
    } catch (error) {
      console.error('Failed to load Topics:', error);
      setPlaylists([]);
      setSelectedPlaylists(new Set());
      setLoadError('Could not load Topics. Confirm PocketBase access and try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedPlaylists(new Set());
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedPlaylists(new Set());
  };

  const togglePlaylist = (playlistId) => {
    setSelectedPlaylists(current => {
      const next = new Set(current);
      if (next.has(playlistId)) next.delete(playlistId);
      else next.add(playlistId);
      return next;
    });
  };

  const toggleAll = () => {
    if (playlists.length > 0 && selectedPlaylists.size === playlists.length) {
      setSelectedPlaylists(new Set());
      return;
    }
    setSelectedPlaylists(new Set(playlists.map(playlist => playlist.id)));
  };

  const archivePlaylist = async (playlist) => {
    if (!window.confirm(`Archive Topic “${playlist.name}”?`)) return;

    try {
      setLoadError('');
      await apiCall(() => pb.collection('playlists').update(playlist.id, { workflow_status: 'archived' }));
      await loadPlaylists();
    } catch (error) {
      console.error('Failed to archive Topic:', error);
      setLoadError('Could not archive this Topic. Please try again.');
    }
  };

  const archiveSelected = async () => {
    if (selectedPlaylists.size === 0) return;
    if (!window.confirm(`Archive ${selectedPlaylists.size} selected Topic(s)?`)) return;

    try {
      setBulkLoading(true);
      setLoadError('');
      await Promise.all(
        Array.from(selectedPlaylists).map(id =>
          apiCall(() => pb.collection('playlists').update(id, { workflow_status: 'archived' }))
        )
      );
      setSelectedPlaylists(new Set());
      await loadPlaylists();
    } catch (error) {
      console.error('Failed to archive selected Topics:', error);
      setLoadError('One or more selected Topics could not be archived. Refresh and try again.');
    } finally {
      setBulkLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFilters(emptyFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedPlaylists(new Set());
  };

  const hasActiveFilters = useMemo(
    () => Boolean(searchTerm) || Object.values(filters).some(Boolean),
    [searchTerm, filters]
  );

  const statusBadge = (status) => {
    const classes = {
      draft: 'bg-slate-700 text-slate-300',
      review: 'bg-yellow-500/20 text-yellow-400',
      published: 'bg-green-500/20 text-green-400',
      archived: 'bg-red-500/20 text-red-400'
    };
    return classes[status] || classes.draft;
  };

  const filterControlClass = 'bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder-slate-400';

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Topics & Playlists</h1>
          <p className="text-slate-400 mt-1">Group related videos by subject. Order the videos when the Topic should also work as a learning sequence.</p>
        </div>
        <Link
          to="/playlists/new"
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors shadow-lg shadow-blue-900/20 whitespace-nowrap"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Create Topic</span>
        </Link>
      </div>

      {loadError && (
        <div className="mb-4 bg-red-500/10 border border-red-500/20 text-red-200 px-4 py-3 rounded-xl flex items-start gap-2 text-sm">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 flex-shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      <div className="bg-slate-900 rounded-xl border border-slate-800 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-800">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search Topics, descriptions or tags..."
                value={searchTerm}
                onChange={(event) => handleSearchChange(event.target.value)}
                className="pl-9 pr-4 py-2 w-full bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder-slate-400 text-sm"
              />
            </div>

            <select value={filters.visibility_scope} onChange={(event) => updateFilter('visibility_scope', event.target.value)} className={filterControlClass}>
              <option value="">All Scopes</option>
              <option value="client_facing">Client Facing</option>
              <option value="bpas_internal_team">Internal Team</option>
            </select>

            <select value={filters.workflow_status} onChange={(event) => updateFilter('workflow_status', event.target.value)} className={filterControlClass}>
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="review">Review</option>
              <option value="published">Published</option>
            </select>

            <div className="flex gap-2 min-w-0">
              <select value={filters.app_id} onChange={(event) => updateFilter('app_id', event.target.value)} className={`flex-1 min-w-0 ${filterControlClass}`}>
                <option value="">All Apps</option>
                {apps.map(app => <option key={app.id} value={app.id}>{app.app_name}</option>)}
              </select>
              {hasActiveFilters && (
                <button type="button" onClick={clearFilters} className="px-3 py-2 text-slate-400 hover:text-white border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors" title="Clear filters">
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {selectedPlaylists.size > 0 && (
          <div className="px-4 py-3 border-b border-slate-800 bg-blue-500/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-blue-200 font-medium text-sm">
              <SafeIcon icon={FiCheck} className="w-4 h-4 text-blue-400" />
              {selectedPlaylists.size} Topic(s) selected
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={archiveSelected}
                disabled={bulkLoading}
                className="bg-red-500/20 text-red-400 border border-red-500/30 px-4 py-2 rounded-lg hover:bg-red-500/30 disabled:opacity-50 flex items-center gap-2 transition-colors text-sm"
              >
                <SafeIcon icon={FiArchive} className="w-4 h-4" />
                {bulkLoading ? 'Archiving...' : 'Archive Selected'}
              </button>
              <button type="button" onClick={() => setSelectedPlaylists(new Set())} className="text-slate-400 hover:text-white text-sm">Cancel</button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-800">
            <thead>
              <tr>
                <th className="px-6 py-4 text-left">
                  <input type="checkbox" checked={playlists.length > 0 && selectedPlaylists.size === playlists.length} onChange={toggleAll} className="h-4 w-4 bg-slate-700 border-slate-600 rounded text-blue-600" />
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Topic</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Scope</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Apps</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Updated</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 bg-slate-900/50">
              {loading ? (
                [...Array(5)].map((_, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4"><div className="h-4 w-4 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-4 w-40 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-6 w-20 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-6 w-20 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-6 w-24 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-4 w-24 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-8 w-24 rounded bg-slate-800 animate-pulse ml-auto" /></td>
                  </tr>
                ))
              ) : playlists.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-14 text-center">
                    <SafeIcon icon={FiList} className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400 font-medium">No Topics found</p>
                    <p className="text-slate-600 text-sm mt-1">Change the filters or create the first Topic for this training library.</p>
                  </td>
                </tr>
              ) : (
                playlists.map(playlist => (
                  <tr key={playlist.id}>
                    <td className="px-6 py-4">
                      <input type="checkbox" checked={selectedPlaylists.has(playlist.id)} onChange={() => togglePlaylist(playlist.id)} className="h-4 w-4 bg-slate-700 border-slate-600 rounded text-blue-600" />
                    </td>
                    <td className="px-6 py-4 max-w-sm">
                      <p className="text-sm font-medium text-white truncate">{playlist.name}</p>
                      <p className="text-xs text-slate-500 truncate mt-1">{playlist.description || 'No description'}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${playlist.visibility_scope === 'client_facing' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>
                        {playlist.visibility_scope === 'client_facing' ? 'Client' : 'Internal'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${statusBadge(playlist.workflow_status)}`}>{playlist.workflow_status}</span>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      {playlist.expand?.app_ids?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {playlist.expand.app_ids.slice(0, 2).map(app => <span key={app.id} className="bg-slate-800 border border-slate-700 text-blue-300 px-2 py-1 rounded text-xs">{app.app_name}</span>)}
                          {playlist.expand.app_ids.length > 2 && <span className="text-xs text-slate-500 py-1">+{playlist.expand.app_ids.length - 2}</span>}
                        </div>
                      ) : <span className="text-xs text-slate-600">Not targeted</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500">
                      {playlist.updated ? format(new Date(playlist.updated), 'dd MMM yyyy, HH:mm') : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-1">
                        <Link to={`/playlists/${playlist.id}`} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg" title="View Topic"><SafeIcon icon={FiEye} className="w-4 h-4" /></Link>
                        <Link to={`/playlists/${playlist.id}/edit`} className="p-2 text-blue-400 hover:text-white hover:bg-blue-600/20 rounded-lg" title="Edit Topic"><SafeIcon icon={FiEdit3} className="w-4 h-4" /></Link>
                        <button type="button" onClick={() => archivePlaylist(playlist)} className="p-2 text-red-400 hover:text-white hover:bg-red-600/20 rounded-lg" title="Archive Topic"><SafeIcon icon={FiArchive} className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm">
            <p className="text-slate-500">{pagination.totalItems} Topic(s)</p>
            <div className="flex items-center gap-2">
              <button type="button" disabled={pagination.page <= 1} onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} className="px-3 py-2 rounded-lg bg-slate-800 text-slate-300 disabled:opacity-40 hover:bg-slate-700">Previous</button>
              <span className="text-slate-400 px-2">Page {pagination.page} of {pagination.totalPages}</span>
              <button type="button" disabled={pagination.page >= pagination.totalPages} onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} className="px-3 py-2 rounded-lg bg-slate-800 text-slate-300 disabled:opacity-40 hover:bg-slate-700">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Playlists;